// Copyright (c) 2025 Digital Asset (Switzerland) GmbH and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import { useCallback, useState } from 'react';
import { Box } from '@mui/material';
import FormDialog from '../../../components/Dialog/FormDialog';
import useCreateProviderConfiguration from '../../../hooks/mutations/registry/configuration/useCreateProviderConfiguration';
import useMutate from '../../../hooks/mutations/useMutate';
import useProviderService from '../../../hooks/queries/registry/onboarding/useProviderService';
import {
  PartyCredentialRequirementElement,
  toPartyCredentialRequirements,
} from '../../../modules/Credential/Common';
import RequiredCredentialsInput from '../../../modules/Credential/RequiredCredentialsInput';
import { ERR_CRE_PROVIDER_CONF, SUC_CRE_PROVIDER_CONF } from '../../../utils/strings';

type DialogProps = {
  open: boolean;
  onClose: () => void;
};

export const CreateProviderConfigurationDialog = ({ open, onClose }: DialogProps) => {
  const [registrarCredentialRequirements, setRegistrarCredentialRequirements] = useState<
    PartyCredentialRequirementElement[]
  >([]);
  const providerService = useProviderService();
  const mutate = useMutate();
  const createProviderConfiguration = useCreateProviderConfiguration();

  const resetState = useCallback(() => {
    setRegistrarCredentialRequirements([]);
  }, [setRegistrarCredentialRequirements]);

  const handleClose = useCallback(() => {
    resetState();
    onClose();
  }, [resetState, onClose]);

  const handleSubmit = useCallback(async () => {
    if (!providerService.data) return;
    const payload = {
      providerServiceCid: providerService.data.contractId,
      holderRequirements: toPartyCredentialRequirements([]),
      registrarRequirements: toPartyCredentialRequirements(registrarCredentialRequirements),
    };
    await mutate(
      createProviderConfiguration,
      payload,
      SUC_CRE_PROVIDER_CONF,
      ERR_CRE_PROVIDER_CONF,
    );
    handleClose();
  }, [
    providerService.data,
    createProviderConfiguration,
    registrarCredentialRequirements,
    handleClose,
    mutate,
  ]);

  const { isLoading } = providerService;
  if (isLoading) return null;

  const disableSubmit = false;

  return (
    <FormDialog
      open={open}
      title="Create Provider Configuration"
      onCancel={handleClose}
      onSubmit={handleSubmit}
      submitName="Create"
      isSubmitDisabled={disableSubmit}
    >
      <Box mb={3}>
        <RequiredCredentialsInput
          list={registrarCredentialRequirements}
          setList={setRegistrarCredentialRequirements}
          title="Required Credentials for Registrars"
        />
      </Box>
    </FormDialog>
  );
};
